import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Briefcase, Building2, Wallet, Heart, Activity, Compass, Sparkles, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { getUserId, fetchUserStatus, clearStatusCache } from "@/lib/subscription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const domainIcons = {
  career: Briefcase,
  business: Building2,
  finance: Wallet,
  relationships: Heart,
  health: Activity,
  ethics: Compass
};

const domainNames = {
  career: "Career & Education",
  business: "Business & Startup",
  finance: "Money & Finance",
  relationships: "Relationships",
  health: "Personal Well-Being",
  ethics: "Ethics & Life Choices"
};

const domainPrompts = {
  career: "I'm considering whether to accept a new job offer, change careers, pursue further education...",
  business: "I'm deciding whether to launch a new product, pivot strategy, take on investment...",
  finance: "I'm weighing whether to make a major purchase, change investment strategy, take on debt...",
  relationships: "I'm thinking about whether to make a commitment, set a boundary, end or change a relationship...",
  health: "I'm considering whether to change my lifestyle, pursue treatment, make a health-related commitment...",
  ethics: "I'm facing a choice that involves my values, long-term identity, or moral principles..."
};

export default function DecisionInputPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const domain = searchParams.get("domain") || "career";
  const [decision, setDecision] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Deci AI state
  const [showDeciAI, setShowDeciAI] = useState(false);
  const [deciInput, setDeciInput] = useState("");
  const [deciLoading, setDeciLoading] = useState(false);
  const [deciResult, setDeciResult] = useState(null);

  const DomainIcon = domainIcons[domain] || Briefcase;
  
  const charCount = deciInput.length;
  const softLimit = 500;
  const isOverLimit = charCount > softLimit;

  const handleDeciAIClarify = async () => {
    if (!deciInput.trim() || deciInput.trim().length < 10) {
      toast.error("Please share more of your thoughts");
      return;
    }
    
    setDeciLoading(true);
    try {
      const response = await axios.post(`${API}/decide/clarify`, {
        raw_text: deciInput.trim()
      });
      setDeciResult(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to clarify. Try again.");
    } finally {
      setDeciLoading(false);
    }
  };

  const handleUseRefinedQuestion = () => {
    if (deciResult) {
      setDecision(deciResult.refined_question);
      // Update domain if suggested
      if (deciResult.suggested_domain && deciResult.suggested_domain !== domain) {
        setSearchParams({ domain: deciResult.suggested_domain });
      }
      setShowDeciAI(false);
      setDeciResult(null);
      setDeciInput("");
      toast.success("Question refined! Review and continue when ready.");
    }
  };

  const handleCloseDeciAI = () => {
    setShowDeciAI(false);
    setDeciResult(null);
    setDeciInput("");
  };

  // User status state
  const [userStatus, setUserStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    // Fetch user status from server
    fetchUserStatus(true).then(status => {
      setUserStatus(status);
      setStatusLoading(false);
    }).catch(() => {
      setStatusLoading(false);
    });
  }, []);

  const handleSubmit = async () => {
    if (!decision.trim() || decision.trim().length < 10) {
      toast.error("Please describe your decision in more detail");
      return;
    }

    // Refresh status before submitting
    const status = await fetchUserStatus(true);
    setUserStatus(status);

    if (!status.can_decide) {
      if (status.needs_payment) {
        toast(status.message || "Payment required to continue.", {
          action: {
            label: "Unlock",
            onClick: () => navigate("/pricing")
          }
        });
      } else {
        toast(status.message || "Decision limit reached.", {
          action: {
            label: "Upgrade",
            onClick: () => navigate("/pricing")
          }
        });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const userId = getUserId();
      const response = await axios.post(`${API}/decision/start-with-user`, {
        decision_text: decision.trim(),
        context: null,
        domain: domain,
        user_id: userId
      });
      
      // Clear status cache after decision started
      clearStatusCache();
      
      navigate(`/framework/${response.data.session_id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response.data?.detail;
        toast(detail?.message || "Decision limit reached", {
          action: {
            label: detail?.needs_payment ? "Unlock" : "Upgrade",
            onClick: () => navigate("/pricing")
          }
        });
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const remaining = userStatus?.remaining ?? 3;
  const limitType = userStatus?.limit_type || 'daily';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-to-home"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <DomainIcon className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground">{domainNames[domain]}</span>
            </div>
            {!statusLoading && (
              <span className="text-[11px] text-muted-foreground/70">
                {remaining} {limitType === 'daily' ? 'left today' : limitType === 'trial' ? 'trial' : 'left this month'}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-xl mx-auto w-full space-y-8"
        >
          {/* Title */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-primary uppercase tracking-wider">
              Step 1 of 4
            </p>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground">
              What decision are you facing?
            </h1>
            <p className="text-muted-foreground">
              Be specific. The more detail you provide, the more useful the analysis.
            </p>
          </div>

          {/* Input */}
          <div className="floating-card p-6">
            <textarea
              data-testid="decision-input"
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={domainPrompts[domain]}
              className="w-full bg-transparent border-none outline-none resize-none text-foreground placeholder:text-muted-foreground/40 text-lg leading-relaxed min-h-[160px]"
              autoFocus
            />
          </div>

          {/* Deci AI Helper Button */}
          <div className="flex items-center gap-3">
            <button
              data-testid="deci-ai-trigger"
              onClick={() => setShowDeciAI(true)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent-foreground transition-colors group"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary/60 group-hover:text-primary transition-colors" />
              <span>Need help clarifying your thoughts?</span>
            </button>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <Button
              data-testid="continue-btn"
              onClick={handleSubmit}
              disabled={isSubmitting || !decision.trim()}
              className="h-11 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm shadow-lg shadow-primary/20"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Deci AI Modal */}
      <AnimatePresence>
        {showDeciAI && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
            onClick={handleCloseDeciAI}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="floating-card w-full max-w-lg p-6 space-y-5"
              onClick={(e) => e.stopPropagation()}
              data-testid="deci-ai-modal"
            >
              {/* Modal Header */}
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h3 className="font-heading text-lg text-foreground">Deci AI</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Share your messy thoughts. I'll help structure them into a clear decision question.
                  </p>
                </div>
                <button
                  data-testid="deci-ai-close"
                  onClick={handleCloseDeciAI}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Input or Result */}
              {!deciResult ? (
                <>
                  <div className="space-y-2">
                    <textarea
                      data-testid="deci-ai-input"
                      value={deciInput}
                      onChange={(e) => setDeciInput(e.target.value)}
                      placeholder="I've been thinking about... there's this opportunity but also... I'm not sure if... and also my situation is..."
                      className="w-full bg-secondary/30 border border-border rounded-lg p-4 outline-none resize-none text-foreground placeholder:text-muted-foreground/40 text-sm leading-relaxed min-h-[120px] focus:border-primary/50 transition-colors"
                      autoFocus
                    />
                    <div className="flex justify-between items-center text-xs">
                      <span className={isOverLimit ? "text-amber-400" : "text-muted-foreground"}>
                        {charCount} / {softLimit} characters
                        {isOverLimit && " — consider being more concise"}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      data-testid="deci-ai-clarify-btn"
                      onClick={handleDeciAIClarify}
                      disabled={deciLoading || !deciInput.trim()}
                      className="h-9 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm"
                    >
                      {deciLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-3.5 w-3.5 mr-2" />
                          Clarify
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Refined Question */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-primary uppercase tracking-wide">Refined Question</p>
                    <p className="text-foreground text-base leading-relaxed bg-secondary/30 border border-border rounded-lg p-3">
                      {deciResult.refined_question}
                    </p>
                  </div>

                  {/* Suggested Domain */}
                  {deciResult.suggested_domain && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Suggested domain:</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {domainNames[deciResult.suggested_domain] || deciResult.suggested_domain}
                      </span>
                    </div>
                  )}

                  {/* Options Identified */}
                  {deciResult.options_identified && deciResult.options_identified.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">Options I heard:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {deciResult.options_identified.map((option, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded bg-secondary/50 text-secondary-foreground border border-border">
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Context */}
                  {deciResult.context_extracted && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">Context noted:</p>
                      <p className="text-sm text-muted-foreground/80 italic">
                        "{deciResult.context_extracted}"
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      onClick={() => setDeciResult(null)}
                      className="h-9 px-4 text-sm text-muted-foreground hover:text-foreground"
                    >
                      Try again
                    </Button>
                    <Button
                      data-testid="deci-ai-use-btn"
                      onClick={handleUseRefinedQuestion}
                      className="h-9 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm"
                    >
                      <Check className="h-3.5 w-3.5 mr-2" />
                      Use this question
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer Footer */}
      <footer className="py-6 px-6">
        <p className="text-[11px] text-muted-foreground/50 text-center">
          Decisora provides structured thinking tools, not professional advice.
        </p>
      </footer>
    </div>
  );
}
